import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useGetIdentity } from '@refinedev/core';
import { Form, Input, Select, Button, Alert, App as AntdApp, Typography, Spin } from 'antd';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AppRole, OrgRole } from '@lebinatikra/shared-types';
import { invitationsApi, type InvitationCreateResponse } from '../../lib/invitations-api';
import { FilterApiError } from '../../lib/filter-api-client';
import { MonoCode } from '@lebinatikra/rtl-utils';

const ORG_ROLES: OrgRole[] = [
  'member',
  'teacher',
  'moderator',
  'student',
  'bachur',
  'parent',
  'avrech',
  'org_admin',
];

interface InvitationFormValues {
  email?: string;
  phone?: string;
  role: OrgRole;
  organization_id: string;
}

interface Identity {
  role: AppRole;
  organizationId: string | null;
  communityId: string | null;
}

export function UserCreate() {
  const { t, i18n } = useTranslation();
  const { notification } = AntdApp.useApp();
  const navigate = useNavigate();
  const { data: identity, isLoading: identityLoading } = useGetIdentity<Identity>();
  const [form] = Form.useForm<InvitationFormValues>();
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (vars: InvitationFormValues) =>
      invitationsApi.create({
        organizationId: vars.organization_id,
        email: vars.email || undefined,
        phone: vars.phone || undefined,
        role: vars.role,
      }),
    onSuccess: (created: InvitationCreateResponse) => {
      notification.success({
        message: t('notifications.invitation_created'),
        description: (
          // rtl-utils:bidi-wrapped
          <MonoCode>{created.invite_code ?? '(see backend)'}</MonoCode>
        ),
      });
      navigate('/users');
    },
    onError: (err: unknown) => {
      if (err instanceof FilterApiError) {
        const localized = i18n.language === 'en' ? err.messageEn : err.message;
        setFormError(localized);
      } else if (err instanceof Error) {
        setFormError(err.message);
      } else {
        setFormError(t('errors.generic'));
      }
    },
  });

  const onFinish = (values: InvitationFormValues) => {
    setFormError(null);
    if (!values.email && !values.phone) {
      setFormError(t('errors.email_or_phone_required'));
      return;
    }
    if (values.email && values.phone) {
      setFormError(t('errors.email_xor_phone'));
      return;
    }
    mutation.mutate(values);
  };

  // Gate render on identity so AntD initialValues reflect the resolved org —
  // initialValues are not re-applied after first mount (AntD caveat).
  if (identityLoading || !identity) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const orgFixed = identity.role === 'org_admin' && identity.organizationId;

  return (
    <div style={{ maxWidth: 560 }}>
      <Typography.Title level={3}>{t('actions.create_invitation')}</Typography.Title>
      {formError ? (
        <Alert
          type="error"
          message={formError}
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setFormError(null)}
        />
      ) : null}
      <Form<InvitationFormValues>
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={
          orgFixed
            ? { organization_id: identity.organizationId ?? undefined, role: 'member' }
            : { role: 'member' }
        }
      >
        <Form.Item
          name="organization_id"
          label={t('fields.organization_id')}
          rules={[{ required: true }]}
        >
          {orgFixed ? (
            <Input disabled dir="ltr" />
          ) : (
            <Input dir="ltr" placeholder="organization uuid" />
          )}
        </Form.Item>

        <Form.Item
          name="email"
          label={t('auth.email')}
          dependencies={['phone']}
          rules={[{ type: 'email' }]}
        >
          <Input dir="ltr" autoComplete="off" />
        </Form.Item>

        <Form.Item name="phone" label={t('fields.phone')} dependencies={['email']}>
          <Input dir="ltr" autoComplete="off" placeholder="+972..." />
        </Form.Item>

        <Form.Item name="role" label={t('fields.role')} rules={[{ required: true }]}>
          <Select options={ORG_ROLES.map((r) => ({ value: r, label: r }))} />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          {t('actions.create_invitation')}
        </Button>
      </Form>
    </div>
  );
}
